<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { useOnline } from '@vueuse/core';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import { useMenuImport } from '@/composition/menu-import/menu-import-context';
  import { useDraftActions } from '@/composition/menu-import/use-draft-actions';
  import { useGtag } from '@/composition/use-gtag';
  import { useUi } from '@/composition/use-ui';
  import {
    CHAT_MESSAGE_MAX,
    assistantMode,
    chatLogKey,
    chatMessage,
    chatSelection,
    isSendKey,
    proposalRevealTarget,
  } from '@/domain/menu-import/assistant-view';
  import type { IListReveal } from '@/domain/menu-import/assistant-view';
  import {
    CHAT_CHIP_KEYS,
    suggestionChips,
  } from '@/domain/menu-import/chat-chips';
  import type { TChatChip } from '@/domain/menu-import/chat-chips';
  import { AlertType } from '@/interfaces/IAlert';
  import type {
    IMenuImportTurn,
    IMenuProposal,
  } from '@/interfaces/IMenuImport';
  import V2MenuProposalCard from './V2MenuProposalCard.vue';

  /**
   * «Pedir cambios» (plan §4.6, D18): the conversation with the assistant.
   * The owner's messages, «Pensando…» while a turn runs, the reply and one
   * proposal card per intent; suggestion chips; the composer (Enter sends,
   * Shift+Enter adds a line) with «Sobre: {name}» while a dish's editor is
   * open. Nothing changes the menu until «Aplicar». The composer is never
   * unmounted (hidden with `v-show` when the chat is unavailable).
   */
  const props = defineProps<{
    /** A read runs: chat requests wait for it. */
    disabled: boolean;
    wide: boolean;
    /** The store's country (price formats). */
    country: string;
    /** The chat is on screen (its tab, and its sheet on narrow screens). */
    active: boolean;
  }>();
  const emit = defineEmits<{
    /** «Ver cambios» on a narrow screen: where the list should scroll. */
    (e: 'reveal', target: IListReveal): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const ctx = useMenuImport();
  const ui = useUi();
  const gtag = useGtag();
  const online = useOnline();
  const { undoAction } = useDraftActions({ country: () => props.country });

  const log = ref<HTMLElement | null>(null);
  /** PosUiTextarea's root is the `<textarea>`. */
  const composer = ref<{ $el: Element } | null>(null);
  const sendButton = ref<HTMLButtonElement | null>(null);
  const text = ref('');
  const sending = ref(false);
  /** The proposal whose apply, discard or retry is being saved (`turnId:proposalId`). */
  const acting = ref<string | null>(null);
  /** The dish whose «Sobre: …» chip the owner removed. */
  const clearedItemId = ref<string | null>(null);
  let alive = true;

  //#region State
  const draft = computed(() => ctx.draft.value);
  const turns = computed(() => ctx.chat.turns.value);
  const thinking = computed(() => ctx.chat.thinking.value);
  const currency = computed(() => ctx.displayCurrency.value);
  const mode = computed(() =>
    assistantMode({ aiEnabled: ctx.aiEnabled.value, reading: props.disabled })
  );
  const ready = computed(() => mode.value === 'ready');
  /** A message can go out now (the text itself aside). */
  const canCompose = computed(
    () =>
      ready.value &&
      online.value &&
      !thinking.value &&
      !sending.value &&
      ctx.sessionId.value !== null
  );
  const canSend = computed(
    () => canCompose.value && chatMessage(text.value) !== null
  );
  const chips = computed<TChatChip[]>(() =>
    draft.value ? suggestionChips(draft.value) : []
  );
  const selection = computed(() =>
    chatSelection(draft.value, ctx.openItemId.value, clearedItemId.value)
  );
  const notice = computed(() => {
    if (mode.value === 'aiOff') return tr('menuImport.chat.aiOff');
    if (mode.value === 'reading')
      return tr('menuImport.chat.unavailableWhileReading');
    return null;
  });
  //#endregion

  //#region Composer
  function composerElement(): HTMLTextAreaElement | null {
    const element = composer.value?.$el;
    return element instanceof HTMLTextAreaElement ? element : null;
  }

  /** 1 to 4 lines: the textarea follows its text (CSS caps the height). */
  function resizeComposer() {
    const element = composerElement();
    if (!element) return;
    element.style.height = 'auto';
    // border-box: scrollHeight leaves out the two 1 px borders.
    element.style.height = `${element.scrollHeight + 2}px`;
  }

  function setText(value: string) {
    text.value = value;
    void nextTick(resizeComposer);
  }

  function composerFocused(): boolean {
    const element = composerElement();
    return !!element && document.activeElement === element;
  }

  function focusComposer() {
    composerElement()?.focus({ preventScroll: true });
  }

  function onKeydown(event: KeyboardEvent) {
    if (!isSendKey(event)) return;
    event.preventDefault();
    void sendTyped();
  }

  function clearSelection() {
    clearedItemId.value = selection.value?.itemId ?? null;
  }
  //#endregion

  //#region Log
  function scrollToEnd() {
    const element = log.value;
    if (element) element.scrollTop = element.scrollHeight;
  }

  async function followLog() {
    await nextTick();
    if (!alive) return;
    scrollToEnd();
  }

  function isThinking(turn: IMenuImportTurn): boolean {
    return turn.status === 'thinking';
  }

  function proposalKey(turn: IMenuImportTurn, proposal: IMenuProposal) {
    return `${turn.turnId}:${proposal.proposalId}`;
  }
  //#endregion

  //#region Sending
  /** Sends a message with the dish of the open editor, if any; false when it did not go out. */
  async function send(message: string, chip: boolean): Promise<boolean> {
    const clean = chatMessage(message);
    if (clean === null || !canCompose.value) return false;
    sending.value = true;
    const selected = selection.value ? [selection.value.itemId] : [];
    const turn = await ctx.chat.send(clean, selected);
    if (!alive) return false;
    sending.value = false;
    if (!turn) return false;
    gtag.event('menu_chat_sent', { chip });
    void followLog();
    return true;
  }

  /** The typed message: the box empties at once and gets the text back when it did not go out. */
  async function sendTyped() {
    if (!canSend.value) return;
    const message = text.value;
    // A keyboard user who pressed the send button gets the composer back.
    const fromButton = document.activeElement === sendButton.value;
    setText('');
    const sent = await send(message, false);
    if (!alive) return;
    if (!sent && text.value === '') setText(message);
    if (fromButton && ready.value) focusComposer();
  }

  function sendChip(chip: TChatChip) {
    void send(tr(CHAT_CHIP_KEYS[chip]), true);
  }
  //#endregion

  //#region Proposals
  async function apply(turn: IMenuImportTurn, proposal: IMenuProposal) {
    if (acting.value !== null) return;
    acting.value = proposalKey(turn, proposal);
    const delta = await ctx.chat.apply(turn.turnId, proposal.proposalId);
    if (!alive) return;
    acting.value = null;
    if (delta) {
      gtag.event('menu_proposal_applied', {
        changes: proposal.effects.length,
      });
      ui.showToast(
        AlertType.Success,
        tr('menuImport.proposal.appliedToast'),
        undoAction(delta.rev)
      );
      return;
    }
    // Refused (the menu changed since): the server's turn carries the proposal as stale.
    void ctx.refresh();
  }

  async function discard(turn: IMenuImportTurn, proposal: IMenuProposal) {
    if (acting.value !== null) return;
    acting.value = proposalKey(turn, proposal);
    const discarded = await ctx.chat.discard(turn.turnId, proposal.proposalId);
    if (!alive) return;
    acting.value = null;
    if (discarded)
      gtag.event('menu_proposal_discarded', {
        changes: proposal.effects.length,
      });
  }

  async function retry(turn: IMenuImportTurn, proposal: IMenuProposal) {
    if (acting.value !== null || !canCompose.value) return;
    acting.value = proposalKey(turn, proposal);
    const next = await ctx.chat.retry(turn.turnId, proposal.proposalId);
    if (!alive) return;
    acting.value = null;
    if (!next) return;
    gtag.event('menu_proposal_retried', { changes: proposal.effects.length });
    void followLog();
  }

  /** «Ver cambios» on a narrow screen: the sheet goes down to 40 % and the list shows the first marked row. */
  function showChanges(proposal: IMenuProposal) {
    if (props.wide) return;
    ctx.sheetHeight.value = 'sm';
    const current = draft.value;
    const target = current ? proposalRevealTarget(proposal, current) : null;
    if (target) emit('reveal', target);
  }
  //#endregion

  //#region Watchers
  // A new turn, or the last one answering: the log follows it.
  watch(
    () => chatLogKey(turns.value),
    () => {
      void followLog();
    }
  );

  // Shown again (tab, sheet): the latest messages; hidden: the keyboard goes.
  watch(
    () => props.active,
    (active) => {
      if (active) {
        void followLog();
        void nextTick(resizeComposer);
      } else if (composerFocused()) {
        composerElement()?.blur();
      }
    }
  );

  // The composer hides when the chat becomes unavailable: let the keyboard go first.
  watch(ready, (isReady) => {
    if (!isReady && composerFocused()) composerElement()?.blur();
  });

  // Another dish opened: its «Sobre: …» chip shows again.
  watch(
    () => ctx.openItemId.value,
    () => {
      clearedItemId.value = null;
    }
  );
  //#endregion

  onMounted(() => {
    scrollToEnd();
  });

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <div class="v2-menu-assistant" :class="{ 'is-docked': wide }">
    <div ref="log" class="chat-log" role="log" aria-live="polite">
      <!-- No invitation to ask while the chat is off: the notice below says so. -->
      <p v-if="mode !== 'aiOff'" class="bubble is-assistant">
        {{ $t('menuImport.chat.intro') }}
      </p>
      <template v-for="turn in turns">
        <p :key="`${turn.turnId}:message`" class="bubble is-owner">
          {{ turn.message }}
        </p>
        <p
          v-if="isThinking(turn)"
          :key="`${turn.turnId}:thinking`"
          class="bubble is-assistant is-thinking"
        >
          <span class="thinking-dots" aria-hidden="true">
            <span class="dot" />
            <span class="dot" />
            <span class="dot" />
          </span>
          <span>{{ $t('menuImport.chat.thinking') }}</span>
        </p>
        <p
          v-else-if="turn.status === 'failed'"
          :key="`${turn.turnId}:failed`"
          class="bubble is-assistant"
        >
          {{ $t('menuImport.chat.failed') }}
        </p>
        <template v-else>
          <p
            v-if="turn.reply"
            :key="`${turn.turnId}:reply`"
            class="bubble is-reply"
          >
            {{ turn.reply }}
          </p>
          <p
            v-if="(turn.droppedProposals || 0) > 0"
            :key="`${turn.turnId}:dropped`"
            class="chat-dropped"
          >
            <PosUiIcon name="information-outline" class="dropped-icon" />
            <span>{{ $t('menuImport.chat.droppedProposals') }}</span>
          </p>
          <template v-if="draft">
            <V2MenuProposalCard
              v-for="proposal in turn.proposals"
              :key="`${turn.turnId}:${proposal.proposalId}`"
              class="chat-proposal"
              :turn="turn"
              :proposal="proposal"
              :draft="draft"
              :currency="currency"
              :country="country"
              :busy="acting !== null"
              :retry-disabled="!canCompose"
              @apply="apply(turn, proposal)"
              @discard="discard(turn, proposal)"
              @retry="retry(turn, proposal)"
              @show-changes="showChanges(proposal)"
            />
          </template>
        </template>
      </template>
    </div>
    <div class="chat-foot">
      <p v-if="notice !== null" class="chat-notice" role="status">
        <PosUiIcon name="information-outline" class="notice-icon" />
        <span>{{ notice }}</span>
      </p>
      <div v-show="ready" class="chat-compose">
        <div v-if="chips.length > 0" class="chat-chips">
          <PosUiChip
            v-for="chip in chips"
            :key="chip"
            class="chat-chip"
            :disabled="!canCompose"
            @click="sendChip(chip)"
            >{{ $t(CHAT_CHIP_KEYS[chip]) }}</PosUiChip
          >
        </div>
        <div v-if="selection" class="chat-about">
          <PosUiIcon name="silverware-fork-knife" class="about-icon" />
          <span class="about-text">{{
            $t('menuImport.chat.about', { name: selection.name })
          }}</span>
          <button
            type="button"
            class="about-clear"
            :aria-label="$t('menuImport.chat.clearSelection')"
            @click="clearSelection"
          >
            <PosUiIcon name="close" :size="18" />
          </button>
        </div>
        <div class="composer">
          <PosUiTextarea
            ref="composer"
            class="composer-input"
            :value="text"
            :rows="1"
            :maxlength="CHAT_MESSAGE_MAX"
            :placeholder="$t('menuImport.chat.placeholder')"
            :aria-label="$t('menuImport.chat.title')"
            @input="setText"
            @keydown.native="onKeydown"
          />
          <button
            ref="sendButton"
            type="button"
            class="composer-send"
            :disabled="!canSend"
            :aria-label="$t('menuImport.chat.send')"
            @mousedown.prevent
            @click="sendTyped"
          >
            <PosUiIcon name="send" :size="20" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-assistant {
    @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col;

    .chat-log {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-y-auto tw-p-4;
      overscroll-behavior: contain;
    }
    .bubble {
      @apply tw-m-0 tw-max-w-[92%] tw-whitespace-pre-line tw-break-words tw-rounded-xl tw-px-3 tw-py-2.5 tw-text-sm;
      &.is-assistant {
        @apply tw-self-start tw-rounded-tl tw-bg-neutral-100 tw-text-neutral-900;
      }
      &.is-owner {
        @apply tw-self-end tw-rounded-tr tw-bg-neutral-900 tw-text-white;
      }
      &.is-reply {
        @apply tw-self-start tw-rounded-tl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-text-neutral-900;
      }
      &.is-thinking {
        @apply tw-flex tw-items-center tw-gap-2 tw-text-neutral-500;
      }
    }
    .thinking-dots {
      @apply tw-flex tw-items-center tw-gap-1;
      .dot {
        @apply tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-neutral-400;
        animation: chat-dot 1.2s ease-in-out infinite;
        &:nth-child(2) {
          animation-delay: 0.15s;
        }
        &:nth-child(3) {
          animation-delay: 0.3s;
        }
      }
    }
    .chat-proposal {
      @apply tw-w-full tw-shrink-0;
    }
    // Under the reply: some changes were too big to show as a proposal.
    .chat-dropped {
      @apply tw-m-0 tw-flex tw-max-w-[92%] tw-items-start tw-gap-1.5 tw-self-start tw-text-xs tw-text-neutral-500;
      .dropped-icon {
        @apply tw-mt-px tw-shrink-0 tw-text-sm;
      }
    }

    .chat-foot {
      @apply tw-flex tw-shrink-0 tw-flex-col tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-bg-white;
    }
    .chat-notice {
      @apply tw-m-0 tw-flex tw-items-start tw-gap-2 tw-px-4 tw-py-3 tw-text-sm tw-text-neutral-500;
      .notice-icon {
        @apply tw-mt-0.5 tw-shrink-0 tw-text-base;
      }
    }
    .chat-compose {
      @apply tw-flex tw-flex-col tw-gap-2 tw-px-3 tw-py-2.5;
    }
    // In the phone sheet the chips scroll sideways; the fading end shows that
    // more follow, and the trailing spacer lets the last chip leave the fade.
    .chat-chips {
      @apply tw-flex tw-gap-2 tw-overflow-x-auto;
      scrollbar-width: none;
      -webkit-mask-image: linear-gradient(
        to right,
        #000 calc(100% - 2rem),
        transparent
      );
      mask-image: linear-gradient(
        to right,
        #000 calc(100% - 2rem),
        transparent
      );
      &::-webkit-scrollbar {
        display: none;
      }
      &::after {
        @apply tw-w-6 tw-shrink-0;
        content: '';
      }
    }
    .chat-chip {
      @apply tw-whitespace-nowrap;
    }
    // Docked beside the menu (a mouse cannot scroll sideways): every chip wraps into view.
    &.is-docked {
      .chat-chips {
        @apply tw-flex-wrap tw-overflow-x-visible;
        -webkit-mask-image: none;
        mask-image: none;
        &::after {
          @apply tw-hidden;
        }
      }
      .chat-chip {
        @apply tw-max-w-full tw-whitespace-normal;
      }
    }
    .chat-about {
      @apply tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-lg tw-bg-neutral-100 tw-pl-3 tw-text-sm tw-text-neutral-700;
      .about-icon {
        @apply tw-shrink-0 tw-text-base tw-text-neutral-500;
      }
      .about-text {
        @apply tw-min-w-0 tw-flex-1 tw-truncate;
      }
      .about-clear {
        @apply tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-neutral-500 hover:tw-text-neutral-900;
        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }
    .composer {
      @apply tw-flex tw-items-end tw-gap-2;
      .composer-input {
        @apply tw-min-w-0 tw-flex-1 tw-overflow-y-auto;
        // One line of 44 px up to four lines; the script follows the text.
        min-height: 44px;
        max-height: 98px;
      }
    }
    // Neutral-900, not orange: the one orange button of the view is «Publicar menú».
    .composer-send {
      @apply tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-neutral-900 tw-text-white hover:tw-bg-neutral-800;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40 tw-ring-offset-1;
      }
      &:disabled {
        @apply tw-cursor-not-allowed tw-bg-neutral-100 tw-text-neutral-400 hover:tw-bg-neutral-100;
      }
    }
  }

  @keyframes chat-dot {
    0%,
    80%,
    100% {
      opacity: 0.3;
    }
    40% {
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-menu-assistant .thinking-dots .dot {
      animation: none;
    }
  }
</style>
